import { CreditReportType } from './credit-report'
import { EquipmentType } from './equipment-types'
import type { EquifaxTrade } from './equifax-credit-report'

export type SolarTradelineParty = 'primary' | 'coborrower'

export type SolarTradelineMatch = {
  customerName: string
  matchedLenderName: string
  balance: number
}

export const SOLAR_TRADELINE_LOAN_AGREEMENT_DOC_TYPE = 'solar_loan_agreement'
export const SOLAR_TRADELINE_PAYOFF_LETTER_DOC_TYPE = 'solar_payoff_letter'

export type SolarTradelineUploadedDoc = {
  containerName: string
  blobName: string
  label?: string
  contentType?: string
  docType?: string
}

export type SolarTradelineInquiryItem = SolarTradelineMatch & {
  id: string
  party: SolarTradelineParty
  isSolarLoan?: boolean
  sameProperty?: boolean
  paidOff?: boolean
  storageOnly?: boolean
  loanAgreementDocs?: SolarTradelineUploadedDoc[]
  payoffLetterDocs?: SolarTradelineUploadedDoc[]
  complete?: boolean
}

export type SolarTradelineItemRequirements = {
  awaitingAnswer: boolean
  needsAgreement: boolean
  needsPayoff: boolean
  shouldDecline: boolean
}

export type SolarTradelineInquiryMergeOptions = {
  /** True when the application equipment type is storage-only. Not an applicant answer. */
  storageOnlyProject: boolean
  allowedContainerName?: string
}

export type SolarTradelineInquiryMergeResult = {
  applied: boolean
  items: SolarTradelineInquiryItem[]
  shouldDecline: boolean
}

export type SolarTradelineDetectionTarget = {
  hasSolarTradeline?: boolean
  solarTradelineInquiryItems?: SolarTradelineInquiryItem[]
}

export type SolarTradelineHoldTarget = Pick<
  SolarTradelineDetectionTarget,
  'solarTradelineInquiryItems'
>

type MatchableTrade = Pick<EquifaxTrade, 'customerName' | 'balance'>

type SolarTradelineReportEnvelope = {
  reportType?: CreditReportType | string
  report?: {
    consumers?: {
      equifaxUSConsumerCreditReport?: Array<{
        trades?: Array<MatchableTrade | null | undefined>
      }>
    }
  }
}

export function solarTradelinePartyFromReportType(
  reportType: CreditReportType | string | null | undefined
): SolarTradelineParty | undefined {
  if (reportType === CreditReportType.Prequal) {
    return 'primary'
  }
  if (reportType === CreditReportType.PrequalCoborrower) {
    return 'coborrower'
  }
  return undefined
}

export function reportTypesForSolarTradelineScan(
  includeCoborrower: boolean
): CreditReportType[] {
  if (includeCoborrower) {
    return [CreditReportType.Prequal, CreditReportType.PrequalCoborrower]
  }
  return [CreditReportType.Prequal]
}

export function isSolarTradelineInquiryComplete(
  target: SolarTradelineHoldTarget | null | undefined
): boolean {
  const items = target?.solarTradelineInquiryItems
  if (!items?.length) {
    return true
  }
  return items.every(item => item.complete)
}

export function isSolarTradelineHoldActive(
  target: SolarTradelineHoldTarget | null | undefined
): boolean {
  return !isSolarTradelineInquiryComplete(target)
}

export function matchActiveSolarLenderTrades(
  trades: Array<MatchableTrade | null | undefined> | null | undefined,
  lenderNames: string[] | null | undefined
): SolarTradelineMatch[] {
  if (!trades?.length || !lenderNames?.length) {
    return []
  }

  const names = lenderNames
    .map(name => name.trim())
    .filter(Boolean)

  if (!names.length) {
    return []
  }

  const matches: SolarTradelineMatch[] = []

  for (const trade of trades) {
    if (!trade) continue

    const balance = trade.balance ?? 0
    if (!(balance > 0)) continue

    const customerName = (trade.customerName || '').trim()
    if (!customerName) continue

    const customerNameLower = customerName.toLowerCase()
    const matchedLenderName = names.find(name => customerNameLower.includes(name.toLowerCase()))
    if (!matchedLenderName) continue

    matches.push({
      customerName,
      matchedLenderName,
      balance,
    })
  }

  return matches
}

function tradesFromEquifaxEnvelope(
  report: SolarTradelineReportEnvelope | null | undefined
): MatchableTrade[] {
  const raw = report?.report?.consumers?.equifaxUSConsumerCreditReport?.[0]
  return (raw?.trades || []).filter((trade): trade is MatchableTrade => Boolean(trade))
}

export function collectSolarTradelineInquiryItems(
  reports: Array<SolarTradelineReportEnvelope | null | undefined> | null | undefined,
  lenderNames: string[] | null | undefined
): SolarTradelineInquiryItem[] {
  if (!reports?.length) {
    return []
  }

  const items: SolarTradelineInquiryItem[] = []
  const sequenceByParty: Record<SolarTradelineParty, number> = {
    primary: 0,
    coborrower: 0,
  }

  for (const report of reports) {
    const party = solarTradelinePartyFromReportType(report?.reportType)
    if (!party) continue

    const matches = matchActiveSolarLenderTrades(tradesFromEquifaxEnvelope(report), lenderNames)
    for (const match of matches) {
      const sequence = sequenceByParty[party]
      sequenceByParty[party] += 1
      items.push({
        id: `${party}-${sequence}`,
        party,
        ...match,
        complete: false,
      })
    }
  }

  return items
}

export function applySolarTradelineDetection(
  target: SolarTradelineDetectionTarget,
  reports: Array<SolarTradelineReportEnvelope | null | undefined> | null | undefined,
  lenderNames: string[] | null | undefined
): boolean {
  if (typeof target.hasSolarTradeline === 'boolean') {
    return false
  }

  const items = collectSolarTradelineInquiryItems(reports, lenderNames)
  target.solarTradelineInquiryItems = items
  target.hasSolarTradeline = items.length > 0
  return true
}

function itemRequirements(
  overrides: Partial<SolarTradelineItemRequirements> = {}
): SolarTradelineItemRequirements {
  return {
    awaitingAnswer: false,
    needsAgreement: false,
    needsPayoff: false,
    shouldDecline: false,
    ...overrides,
  }
}

/**
 * Storage-only is an internal exception from the application equipment type.
 * Applicants are not asked. An unpaid solar loan on the same property declines
 * unless this project is storage-only, which still requires the loan agreement.
 */
export function solarTradelineItemRequirements(
  item: Pick<SolarTradelineInquiryItem, 'isSolarLoan' | 'sameProperty' | 'paidOff'>,
  storageOnlyProject: boolean
): SolarTradelineItemRequirements {
  if (typeof item.isSolarLoan !== 'boolean') {
    return itemRequirements({ awaitingAnswer: true })
  }

  if (!item.isSolarLoan) {
    return itemRequirements()
  }

  if (typeof item.sameProperty !== 'boolean') {
    return itemRequirements({ awaitingAnswer: true })
  }

  if (!item.sameProperty) {
    return itemRequirements({ needsAgreement: true })
  }

  if (typeof item.paidOff !== 'boolean') {
    return itemRequirements({ awaitingAnswer: true })
  }

  if (item.paidOff) {
    return itemRequirements({ needsAgreement: true, needsPayoff: true })
  }

  if (storageOnlyProject) {
    return itemRequirements({ needsAgreement: true })
  }

  return itemRequirements({ shouldDecline: true })
}

export function isSolarTradelineDecline(
  target: (SolarTradelineHoldTarget & { equipmentType?: EquipmentType }) | null | undefined
): boolean {
  const items = target?.solarTradelineInquiryItems
  if (!items?.length) {
    return false
  }

  const storageOnlyProject = target.equipmentType === EquipmentType.StorageOnly
  return items.some(item => (
    solarTradelineItemRequirements(item, storageOnlyProject).shouldDecline
  ))
}

function readBoolean(value: unknown): boolean | undefined {
  if (value === true || value === false) {
    return value
  }
  return undefined
}

function normalizeSolarTradelineDocs(
  docs: unknown,
  docType: string,
  allowedContainerName?: string
): SolarTradelineUploadedDoc[] {
  if (!Array.isArray(docs)) {
    return []
  }

  const normalized: SolarTradelineUploadedDoc[] = []

  for (const doc of docs) {
    if (!doc || typeof doc !== 'object') {
      continue
    }

    const { containerName, blobName, label, contentType } = doc as {
      containerName?: unknown
      blobName?: unknown
      label?: unknown
      contentType?: unknown
    }

    if (typeof containerName !== 'string' || !containerName) {
      continue
    }
    if (typeof blobName !== 'string' || !blobName) {
      continue
    }
    if (allowedContainerName && containerName !== allowedContainerName) {
      continue
    }

    const next: SolarTradelineUploadedDoc = {
      containerName,
      blobName,
      docType,
    }
    if (typeof label === 'string' && label) {
      next.label = label
    }
    if (typeof contentType === 'string' && contentType) {
      next.contentType = contentType
    }
    normalized.push(next)
  }

  return normalized
}

function hasPersistedSolarTradelineDoc(
  docs: SolarTradelineUploadedDoc[] | undefined
): boolean {
  return Boolean(docs?.some(doc => doc.containerName && doc.blobName))
}

function isSolarTradelineItemComplete(
  item: SolarTradelineInquiryItem,
  storageOnlyProject: boolean
): boolean {
  const requirements = solarTradelineItemRequirements(item, storageOnlyProject)
  if (requirements.awaitingAnswer || requirements.shouldDecline) {
    return false
  }
  if (requirements.needsAgreement && !hasPersistedSolarTradelineDoc(item.loanAgreementDocs)) {
    return false
  }
  if (requirements.needsPayoff && !hasPersistedSolarTradelineDoc(item.payoffLetterDocs)) {
    return false
  }
  return true
}

function identityFromInquiryItem(item: SolarTradelineInquiryItem): SolarTradelineInquiryItem {
  return {
    id: item.id,
    party: item.party,
    customerName: item.customerName,
    matchedLenderName: item.matchedLenderName,
    balance: item.balance,
  }
}

type InquiryAnswerSource = {
  isSolarLoan?: unknown
  sameProperty?: unknown
  paidOff?: unknown
  loanAgreementDocs?: unknown
  payoffLetterDocs?: unknown
}

function asAnswerSource(value: unknown): InquiryAnswerSource {
  if (!value || typeof value !== 'object') {
    return {}
  }
  return value as InquiryAnswerSource
}

function readIncomingId(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  const id = (value as { id?: unknown }).id
  if (typeof id !== 'string' || !id) {
    return undefined
  }
  return id
}

function applyInquiryAnswer(
  stored: SolarTradelineInquiryItem,
  incoming: unknown,
  options: SolarTradelineInquiryMergeOptions
): SolarTradelineInquiryItem {
  const source = asAnswerSource(incoming ?? stored)
  const next = identityFromInquiryItem(stored)
  const isSolarLoan = readBoolean(source.isSolarLoan)
  const sameProperty = readBoolean(source.sameProperty)
  const paidOff = readBoolean(source.paidOff)

  if (typeof isSolarLoan === 'boolean') {
    next.isSolarLoan = isSolarLoan
  }
  if (next.isSolarLoan === true && typeof sameProperty === 'boolean') {
    next.sameProperty = sameProperty
  }
  if (next.isSolarLoan === true && next.sameProperty === true && typeof paidOff === 'boolean') {
    next.paidOff = paidOff
  }
  if (next.isSolarLoan === true && next.sameProperty === true && next.paidOff === false) {
    next.storageOnly = options.storageOnlyProject
  }

  const requirements = solarTradelineItemRequirements(next, options.storageOnlyProject)
  next.loanAgreementDocs = requirements.needsAgreement
    ? normalizeSolarTradelineDocs(
      source.loanAgreementDocs,
      SOLAR_TRADELINE_LOAN_AGREEMENT_DOC_TYPE,
      options.allowedContainerName
    )
    : []
  next.payoffLetterDocs = requirements.needsPayoff
    ? normalizeSolarTradelineDocs(
      source.payoffLetterDocs,
      SOLAR_TRADELINE_PAYOFF_LETTER_DOC_TYPE,
      options.allowedContainerName
    )
    : []
  next.complete = isSolarTradelineItemComplete(next, options.storageOnlyProject)
  return next
}

export function mergeSolarTradelineInquiryItems(
  existing: SolarTradelineInquiryItem[] | null | undefined,
  incoming: unknown,
  options: SolarTradelineInquiryMergeOptions
): SolarTradelineInquiryMergeResult {
  if (!existing?.length || !Array.isArray(incoming)) {
    return {
      applied: false,
      items: existing ? [...existing] : [],
      shouldDecline: false,
    }
  }

  const incomingById = new Map<string, unknown>()
  for (const item of incoming) {
    const id = readIncomingId(item)
    if (id) {
      incomingById.set(id, item)
    }
  }

  const items = existing.map(stored => applyInquiryAnswer(
    stored,
    incomingById.get(stored.id) ?? stored,
    options
  ))
  const shouldDecline = items.some(item => (
    solarTradelineItemRequirements(item, options.storageOnlyProject).shouldDecline
  ))

  return {
    applied: true,
    items,
    shouldDecline,
  }
}
